import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { API_URL } from "../../../../api";
import { useCookies } from "../../../../hooks/useCookies";
import { getValidWaves, getStartWave, toastWaveChange } from "./waveLogic";

const UserProfileContext = createContext();

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error("useUserProfile must be used within a UserProfileProvider");
  }
  return context;
};

export default function UserProfileProvider({ children }) {
  const cookies = useCookies();

  const [profiles, setProfiles] = useState([]);
  const [token, setToken] = useState(cookies.getCookie("token"));
  const [isPremium, setIsPremium] = useState(null); // null = not known yet
  const [wave, setWave] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(1);

  const canAutoRefetch = useRef(true);

  console.log("[UserProfileProvider] mounted");

  // Load isPremium from cookies on mount
  useEffect(() => {
    const premiumValue = cookies.getCookie("isPremium") === "true";
    setIsPremium(premiumValue);
    setWave(getStartWave(premiumValue));
  }, []); // run once

  // Keep checking if token is set after login
  useEffect(() => {
    const interval = setInterval(() => {
      const currentToken = cookies.getCookie("token");
      if (currentToken && currentToken !== token) {
        setToken(currentToken);
        clearInterval(interval);
      }
    }, 300);

    return () => clearInterval(interval);
  }, [token, cookies]);

  // Keep checking if isPremium changes (upgrade / downgrade)
  const isPremiumRef = useRef(isPremium);
  useEffect(() => {
    isPremiumRef.current = isPremium;
  }, [isPremium]);

  useEffect(() => {
    const interval = setInterval(() => {
      const currentPremium = cookies.getCookie("isPremium") === "true";
      if (isPremiumRef.current !== currentPremium) {
        setIsPremium(currentPremium);
        setWave(getStartWave(currentPremium));
      }
    }, 300);

    return () => clearInterval(interval);
  }, [cookies]);

  const removeProfile = (userId) => {
    setProfiles((prevProfiles) => {
      const updated = prevProfiles.filter((p) => p.user_id !== userId);

      if (updated.length <= 5 && canAutoRefetch.current) {
        canAutoRefetch.current = false;
        setRefresh((prev) => prev + 1);
      }

      return updated;
    });
  };

  // 🔑 Core: fetch FIRST PAGE of discovery feed with proper wave progression
  useEffect(() => {
    if (!token || isPremium === null) return;

    const controller = new AbortController();

    const fetchFirstPageWithWaves = async () => {
      setLoading(true);
      try {
        const validWaves = getValidWaves(isPremium);
        if (!validWaves || validWaves.length === 0) {
          setProfiles([]);
          return;
        }

        let currentWave = getStartWave(isPremium);

        // Snap to a valid wave just in case
        if (!validWaves.includes(currentWave)) {
          currentWave = validWaves[0];
        }

        while (true) {
          const response = await fetch(
            `${API_URL}customer/user/profiles?page=1&wave=${currentWave}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
              },
              signal: controller.signal,
            }
          );

          const data = await response.json();
          const results = data?.results || [];

          if (response.ok && results.length > 0) {
            // Frontend Shuffle: Randomize the order of profiles before displaying
            for (let i = results.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [results[i], results[j]] = [results[j], results[i]];
            }

            setProfiles(results);
            setWave(currentWave);
            toastWaveChange(currentWave);
            break;
          } else {
            const idx = validWaves.indexOf(currentWave);
            const nextWave = validWaves[idx + 1];

            if (nextWave == null) {
              // no more waves
              setProfiles([]);
              break;
            }

            currentWave = nextWave;
          }
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("UserProfileProvider: Fetch error:", error);
        }
        setProfiles([]);
      } finally {
        setLoading(false);
        canAutoRefetch.current = true;
      }
    };

    fetchFirstPageWithWaves();

    return () => controller.abort();
  }, [token, isPremium, refresh]);

  const clearProfiles = () => {
    setProfiles([]);
  };

  const value = useMemo(
    () => ({
      profiles,
      setProfiles,
      loading,
      setLoading,
      wave,
      setWave, // usually only provider updates this, but exposed just in case
      refresh,
      setRefresh,
      removeProfile,
      clearProfiles,
      token,
      isPremium,
    }),
    [profiles, loading, wave, refresh, token, isPremium]
  );

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
}
